import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import {
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { sanitizeInput } from '@repo/utils';
import {
  CASH_PICKUP_NATIONS,
  HKG_BANK_LIST,
  HKG_INFO_LIST,
  SGP_BANK_LIST,
} from '@repo/constants';
import { ICON_CHEVRON_DOWN_GRAY, ICON_WARNING } from '@repo/assets';
import {
  FlexWrap,
  Icon,
  InfoCHN,
  InfoJPN,
  Input,
  SwiftCode,
  Typography,
} from '@repo/components';
import { AccountInfo } from '@repo/types';
import { CashPickupGuideContent } from '@repo/components';

type BankInfoFormProps = {
  errors?: FieldErrors;
  register: UseFormRegister<AccountInfo>;
  getValues: UseFormGetValues<AccountInfo>;
  selectedAccountInfo: string;
  setSelectedAccountInfo: Dispatch<SetStateAction<string>>;
  selectedBank: string;
  setSelectedBank: Dispatch<SetStateAction<string>>;
  selectedAccountType: string;
  setSelectedAccountType: Dispatch<SetStateAction<string>>;
  disabled?: boolean;
  isEdit?: boolean;
  setValue: UseFormSetValue<AccountInfo>;
  selectedGender: string;
  setSelectedGender: Dispatch<SetStateAction<string>>;
  isBankCardInquiry: boolean;
  setIsBankCardInquiry: Dispatch<SetStateAction<boolean>>;
  isRefundInfoPage?: boolean;
  selectedRegion: string;
  setSelectedRegion: Dispatch<SetStateAction<string>>;
  selectedTown: string;
  setSelectedTown: Dispatch<SetStateAction<string>>;
  isHkgNation: boolean;
  setIsHkgNation?: Dispatch<SetStateAction<boolean>>;
  nationality: string;
  paymentStatus?: string;
  isSwiftCodeRequired: boolean;
};

export const InfoForm = ({
  errors,
  register,
  getValues,
  selectedAccountInfo,
  setSelectedAccountInfo,
  selectedBank,
  setSelectedBank,
  selectedAccountType,
  setSelectedAccountType,
  disabled,
  isEdit,
  setValue,
  selectedGender,
  setSelectedGender,
  isBankCardInquiry,
  setIsBankCardInquiry,
  isRefundInfoPage,
  selectedRegion,
  setSelectedRegion,
  selectedTown,
  setSelectedTown,
  isHkgNation,
  setIsHkgNation,
  nationality,
  paymentStatus,
  isSwiftCodeRequired,
}: BankInfoFormProps) => {
  let isIntital = useRef(false);

  useEffect(() => {
    if (selectedTown !== 'Direct Input') {
      setValue('directInput', '');
    }
  }, [selectedTown]);

  useEffect(() => {
    if (isHkgNation && isIntital.current) {
      setValue('city', '');
      setValue('directInput', '');
      setSelectedTown('');
    }
  }, [selectedRegion]);

  const remittanceMethodInfo = () => {
    switch (nationality) {
      case 'MAC':
      case 'CHN':
        return (
          <InfoCHN
            errors={errors}
            register={register}
            getValues={getValues}
            setValue={setValue}
            disabled={disabled}
            isHkgNation={isHkgNation}
            setIsHkgNation={setIsHkgNation}
            selectedGender={selectedGender}
            setSelectedGender={setSelectedGender}
            isBankCardInquiry={isBankCardInquiry}
            setIsBankCardInquiry={setIsBankCardInquiry}
            isRefundInfoPage={isRefundInfoPage}
            paymentStatus={paymentStatus}
          />
        );
      case 'HKG':
        return (
          <>
            <FlexWrap dir='column'>
              <LabelWrapper>
                <Label>Bank account</Label>
                <RequiredDot>*</RequiredDot>
              </LabelWrapper>
              <BankWrap isChnBank>
                <ArrowIcon src={ICON_CHEVRON_DOWN_GRAY} isChnBank />
                <Select
                  {...register('bank', {
                    required: 'This is a required item.',
                  })}
                  isSelected={Boolean(selectedBank)}
                  onChange={(e) => setSelectedBank(e.target.value)}
                  isEdit={isEdit}
                  disabled={disabled}
                >
                  <Option value='' hidden>
                    Name of Bank
                  </Option>
                  {HKG_BANK_LIST.map((bank, idx) => (
                    <Option key={idx} value={bank}>
                      {bank}
                    </Option>
                  ))}
                </Select>
              </BankWrap>
            </FlexWrap>
            <Input
              placeholder='Bank account'
              {...register('bankAccount', {
                pattern: {
                  value: /^\S/,
                  message: 'This is a required item.',
                },
              })}
              type='number'
              error={errors?.bankAccount}
              disabled={disabled}
            />
            <Typography
              size='14px'
              lineHeight='20px'
              fontWeight='400'
              color='#A7A7A7'
              margin='8px 0 0 0'
            >
              * Corporate accounts are not accepted.
            </Typography>

            {!isRefundInfoPage && (
              <Typography
                size='13px'
                lineHeight='19.5px'
                fontWeight='400'
                color='#246CF6'
                margin='20px 0 0 0'
                cursor='pointer'
                onClick={() => setIsHkgNation && setIsHkgNation((v) => !v)}
              >
                向中国账户汇款 &gt;
              </Typography>
            )}

            {/* Region */}
            <FlexWrap dir='column' margin='20px 0 0 0'>
              <LabelWrapper>
                <Label>Region</Label>
                <RequiredDot>*</RequiredDot>
              </LabelWrapper>
              <BankWrap isChnBank>
                <ArrowIcon src={ICON_CHEVRON_DOWN_GRAY} isChnBank />
                <Select
                  {...register('stateProvinceRegion', {
                    required: 'This is a required item.',
                  })}
                  isSelected={Boolean(selectedRegion)}
                  onChange={(e) => {
                    setSelectedRegion(e.target.value);
                    isIntital.current = true;
                  }}
                  isEdit={isEdit}
                  disabled={disabled}
                >
                  <Option value='' hidden>
                    Hongkong / Kowlong / The New Territories
                  </Option>
                  {Object.keys(HKG_INFO_LIST)?.map((region, idx) => (
                    <Option key={idx} value={region}>
                      {region}
                    </Option>
                  ))}
                </Select>
              </BankWrap>
            </FlexWrap>
            {/* Town or District */}
            <FlexWrap dir='column' margin='20px 0 0 0'>
              <LabelWrapper>
                <Label>Town or District</Label>
                <RequiredDot>*</RequiredDot>
              </LabelWrapper>
              <BankWrap isChnBank>
                <ArrowIcon src={ICON_CHEVRON_DOWN_GRAY} isChnBank />
                <Select
                  {...register('city', {
                    required: 'This is a required item.',
                  })}
                  isSelected={Boolean(selectedTown)}
                  onChange={(e) => setSelectedTown(e.target.value)}
                  isEdit={isEdit}
                  disabled={disabled}
                >
                  <Option value='' hidden>
                    Town or District
                  </Option>
                  {HKG_INFO_LIST[selectedRegion]?.streetList.map(
                    (street, idx) => (
                      <Option key={idx} value={street}>
                        {street}
                      </Option>
                    ),
                  )}
                </Select>
              </BankWrap>
            </FlexWrap>
            {selectedTown === 'Direct Input' && (
              <Input
                {...register('directInput', {
                  required: 'This is a required item.',
                  pattern: {
                    value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                    message: 'Please enter only English and numbers.',
                  },
                  onChange: (e) =>
                    setValue('directInput', sanitizeInput(e.target.value)),
                })}
                type='text'
                isRequired
                placeholder='Please enter your town or district in English'
                error={errors?.directInput}
                disabled={disabled}
              />
            )}
            <Input
              {...register('address', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('address', sanitizeInput(e.target.value)),
              })}
              type='text'
              isRequired
              label='Street name'
              placeholder='Name of street'
              error={errors?.address}
              margin={[20, 0, 0]}
              disabled={disabled}
            />
            <Input
              {...register('addressDetail', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('addressDetail', sanitizeInput(e.target.value)),
              })}
              type='text'
              isRequired
              label='Building number '
              placeholder='Building number'
              error={errors?.addressDetail}
              margin={[20, 0, 0]}
              disabled={disabled}
            />
            <Input
              {...register('addressDetail2', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('addressDetail2', sanitizeInput(e.target.value)),
              })}
              type='text'
              isRequired
              label='Name of Building'
              placeholder='Name of Building'
              error={errors?.addressDetail2}
              margin={[20, 0, 0]}
              disabled={disabled}
            />
          </>
        );
      case 'USA':
        return (
          <>
            <Input
              {...register('bank', {
                required: 'This is a required item.',
                onChange: (e) =>
                  setValue('bank', sanitizeInput(e.target.value)),
              })}
              label='Bank account'
              placeholder='Name of Bank'
              type='text'
              error={errors?.bank}
              disabled={disabled}
              isRequired
            />
            <Input
              {...register('bankAccount', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter only numbers',
                },
                onChange: (e) =>
                  setValue('bankAccount', sanitizeInput(e.target.value)),
              })}
              type='text'
              placeholder='Bank account'
              error={errors?.bankAccount}
              margin={[8, 0, 0]}
              disabled={disabled}
            />
            <Typography
              size='12px'
              lineHeight='20px'
              fontWeight='400'
              color='#A7A7A7'
              margin='8px 0 0 0'
            >
              * Corporate accounts are not accepted.
            </Typography>
            <Input
              {...register('routingNumber', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter only numbers',
                },
              })}
              type='text'
              label='ACH routing number'
              error={errors?.routingNumber}
              isRequired
              maxLength={9}
              placeholder='ACH routing number (9-digits)'
              margin={[20, 0, 0]}
              disabled={disabled}
            />
            <SwiftCode
              register={register}
              errors={errors}
              disabled={disabled}
            />
            <FlexWrap dir='column' margin='20px 0 0 0'>
              <LabelWrapper>
                <Label>Account Type</Label>
                <RequiredDot>*</RequiredDot>
              </LabelWrapper>
              <FlexWrap dir='column' justifyContent='center' gap='16px'>
                <RadioLabel>
                  <input
                    {...register('accountInfo', {
                      required: 'This is a required item.',
                    })}
                    type='radio'
                    value='SAVING'
                    onClick={() => setSelectedAccountInfo('SAVING')}
                    checked={selectedAccountInfo === 'SAVING'}
                    disabled={disabled}
                  />
                  Saving Account
                </RadioLabel>
                <RadioLabel>
                  <input
                    {...register('accountInfo', {
                      required: 'This is a required item.',
                    })}
                    type='radio'
                    value='CHECKING'
                    onClick={() => setSelectedAccountInfo('CHECKING')}
                    checked={selectedAccountInfo === 'CHECKING'}
                    disabled={disabled}
                  />
                  Checking Account
                </RadioLabel>
                {errors?.accountInfo && (
                  <FlexWrap gap='8px'>
                    <Icon imgUrl={ICON_WARNING} width='18px' height='18px' />
                    <Typography color='#f13e4b' size='13px' fontWeight='400'>
                      This is a required item.
                    </Typography>
                  </FlexWrap>
                )}
              </FlexWrap>
            </FlexWrap>
            <Input
              {...register('country', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue('country', sanitizeInput(e.target.value)),
              })}
              label='Country'
              isRequired
              placeholder='Country'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.country}
            />
            <Input
              {...register('stateProvinceRegion', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue(
                    'stateProvinceRegion',
                    sanitizeInput(e.target.value),
                  ),
              })}
              label='State'
              isRequired
              placeholder='State'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.stateProvinceRegion}
            />
            <Input
              {...register('city', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue('city', sanitizeInput(e.target.value)),
              })}
              label='City'
              isRequired
              placeholder='City'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.city}
            />
            <Input
              {...register('address', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('address', sanitizeInput(e.target.value)),
              })}
              label='Recipient Address'
              isRequired
              placeholder='Recipient Address'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.address}
            />
            <Input
              {...register('zipPostalCode', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('zipPostalCode', sanitizeInput(e.target.value)),
              })}
              label='Post code'
              isRequired
              placeholder='Post code'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.zipPostalCode}
            />
          </>
        );
      case 'JPN':
        return (
          <InfoJPN
            register={register}
            getValues={getValues}
            setValue={setValue}
            errors={errors}
            disabled={disabled}
            selectedAccountType={selectedAccountType}
            setSelectedAccountType={setSelectedAccountType}
          />
        );
      case 'THA':
        return (
          <>
            <Input
              {...register('bank', {
                required: 'This is a required item.',
                onChange: (e) =>
                  setValue('bank', sanitizeInput(e.target.value)),
              })}
              label='Bank account'
              placeholder='Name of Bank'
              type='text'
              error={errors?.bank}
              disabled={disabled}
              isRequired
            />
            <Input
              {...register('bankAccount', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter only numbers',
                },
                onChange: (e) =>
                  setValue('bankAccount', sanitizeInput(e.target.value)),
              })}
              type='text'
              placeholder='Bank account'
              error={errors?.bankAccount}
              margin={[8, 0, 0]}
              disabled={disabled}
            />
            <Typography
              size='12px'
              lineHeight='20px'
              fontWeight='400'
              color='#A7A7A7'
              margin='8px 0 0 0'
            >
              * บัญชีธนาคารของบริษัทไม่ได้รับการยอมรับ
            </Typography>
            <SwiftCode
              register={register}
              errors={errors}
              disabled={disabled}
            />
            <Input
              {...register('stateProvinceRegion', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
              })}
              label='จังหวัด'
              isRequired
              placeholder='Province (ภาษาอังกฤษเท่านั้น English only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.stateProvinceRegion}
            />
            <Input
              {...register('city', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
              })}
              label='เขต'
              isRequired
              placeholder='District (ภาษาอังกฤษเท่านั้น English only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.city}
            />
            <Input
              {...register('address', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
              })}
              label='ตำบล. ถนน'
              isRequired
              placeholder='Subdistrict. Road'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.address}
            />
            <Input
              {...register('addressDetail', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
              })}
              label='แปลง/เลขที่บ้าน. หมู่บ้าน'
              isRequired
              placeholder='Plot/House number. Village'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.addressDetail}
            />
            <Input
              {...register('zipPostalCode', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
              })}
              label='รหัสไปรษณีย์'
              isRequired
              placeholder='Post code'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.zipPostalCode}
            />
          </>
        );
      case 'AUS':
        return (
          <>
            <Input
              {...register('bsbNumber', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter only numbers',
                },
              })}
              type='number'
              label='BSB code'
              error={errors?.bsbNumber}
              maxLength={6}
              isRequired
              placeholder='BSB code (6-digits)'
              disabled={disabled}
            />
            <Input
              placeholder='Bank account'
              {...register('bankAccount', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter only numbers',
                },
                onChange: (e) =>
                  setValue('bankAccount', sanitizeInput(e.target.value)),
              })}
              isRequired
              label='Bank account'
              type='number'
              margin={[20, 0, 0]}
              error={errors?.bankAccount}
              disabled={disabled}
            />
            <Input
              {...register('stateProvinceRegion', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue(
                    'stateProvinceRegion',
                    sanitizeInput(e.target.value),
                  ),
              })}
              label='State'
              isRequired
              placeholder='State'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.stateProvinceRegion}
            />
            <Input
              {...register('city', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue('city', sanitizeInput(e.target.value)),
              })}
              label='City or Suburb'
              isRequired
              placeholder='City or Suburb'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.city}
            />
            <Input
              {...register('addressDetail', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('addressDetail', sanitizeInput(e.target.value)),
              })}
              label='Street No.'
              isRequired
              placeholder='Street No.'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.addressDetail}
            />
            <Input
              {...register('addressDetail2', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('addressDetail2', sanitizeInput(e.target.value)),
              })}
              label='APT, UNIT or SUITE'
              isRequired
              placeholder='APT, UNIT or SUITE'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.addressDetail2}
            />
            <Input
              {...register('zipPostalCode', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('zipPostalCode', sanitizeInput(e.target.value)),
              })}
              label='Post code'
              isRequired
              placeholder='Post code'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.zipPostalCode}
            />
          </>
        );
      case 'CAN':
        return (
          <>
            <Input
              {...register('bank', {
                required: 'This is a required item.',
                onChange: (e) =>
                  setValue('bank', sanitizeInput(e.target.value)),
              })}
              label='Bank account'
              placeholder='Name of Bank'
              type='text'
              error={errors?.bank}
              disabled={disabled}
              isRequired
            />
            <Input
              {...register('bankAccount', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter only numbers',
                },
                onChange: (e) =>
                  setValue('bankAccount', sanitizeInput(e.target.value)),
              })}
              type='text'
              placeholder='Bank account'
              error={errors?.bankAccount}
              margin={[8, 0, 0]}
              disabled={disabled}
            />
            <Typography
              size='12px'
              lineHeight='20px'
              fontWeight='400'
              color='#A7A7A7'
              margin='8px 0 20px 0'
            >
              * Corporate accounts are not accepted.
            </Typography>
            <FlexWrap dir='column'>
              <LabelWrapper>
                <Label>Account Type</Label>
                <RequiredDot>*</RequiredDot>
              </LabelWrapper>
              <FlexWrap dir='column' justifyContent='center' gap='16px'>
                <RadioLabel>
                  <input
                    {...register('accountInfo', {
                      required: 'This is a required item.',
                    })}
                    type='radio'
                    value='SAVING'
                    onClick={(e) => setSelectedAccountInfo('SAVING')}
                    checked={selectedAccountInfo === 'SAVING'}
                    disabled={disabled}
                  />
                  Saving Account
                </RadioLabel>
                <RadioLabel>
                  <input
                    {...register('accountInfo', {
                      required: 'This is a required item.',
                    })}
                    type='radio'
                    value='CHECKING'
                    onClick={() => setSelectedAccountInfo('CHECKING')}
                    checked={selectedAccountInfo === 'CHECKING'}
                    disabled={disabled}
                  />
                  Checking Account
                </RadioLabel>
                {errors?.accountInfo && (
                  <FlexWrap gap='8px'>
                    <Icon imgUrl={ICON_WARNING} width='18px' height='18px' />
                    <Typography color='#f13e4b' size='13px' fontWeight='400'>
                      This is a required item.
                    </Typography>
                  </FlexWrap>
                )}
              </FlexWrap>
            </FlexWrap>
            <Input
              {...register('transitNumber', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter only numbers',
                },
              })}
              label='Transit Number'
              isRequired
              maxLength={5}
              placeholder='Transit Number (5-digit)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.transitNumber}
            />
            <Input
              {...register('institutionNumber', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter only numbers',
                },
              })}
              label='Institution number'
              isRequired
              placeholder='Institution number (3-digit)'
              maxLength={3}
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.institutionNumber}
            />
            <Typography
              size='12px'
              lineHeight='20px'
              fontWeight='400'
              color='#A7A7A7'
              margin='8px 0 0 0'
            >
              * The institution number is a 3-digit number identifying the
              financial institution to which a payment item is directed.
            </Typography>
            <Input
              {...register('stateProvinceRegion', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
              })}
              label='Province or Territory'
              isRequired
              placeholder='Province or Territory (English only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.stateProvinceRegion}
            />
            <Input
              {...register('city', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue('city', sanitizeInput(e.target.value)),
              })}
              label='City or Suburb'
              isRequired
              placeholder='City or Suburb'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.city}
            />
            <Input
              {...register('addressDetail', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('addressDetail', sanitizeInput(e.target.value)),
              })}
              label='Street No.'
              isRequired
              placeholder='Street No.'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.addressDetail}
            />
            <Input
              {...register('addressDetail2', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('addressDetail2', sanitizeInput(e.target.value)),
              })}
              label='APT, UNIT or SUITE'
              isRequired
              placeholder='APT, UNIT or SUITE'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.addressDetail2}
            />
            <Input
              {...register('zipPostalCode', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('zipPostalCode', sanitizeInput(e.target.value)),
              })}
              label='Post code'
              isRequired
              placeholder='Post code'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.zipPostalCode}
            />
          </>
        );
      case 'SGP':
        return (
          <>
            <LabelWrapper>
              <Label>Bank account</Label>
              <RequiredDot>*</RequiredDot>
            </LabelWrapper>
            <BankWrap isChnBank>
              <ArrowIcon src={ICON_CHEVRON_DOWN_GRAY} isChnBank />
              <Select
                {...register('bank')}
                isSelected={Boolean(selectedBank)}
                onChange={(e) => setSelectedBank(e.target.value)}
                isEdit={isEdit}
                disabled={disabled}
              >
                <Option value='' hidden>
                  Name of Bank
                </Option>
                {SGP_BANK_LIST.map((bank, idx) => (
                  <Option key={idx} value={bank}>
                    {bank}
                  </Option>
                ))}
              </Select>
            </BankWrap>
            <Input
              placeholder='Bank account'
              {...register('bankAccount', {
                pattern: {
                  value: /^\S/,
                  message: 'This is a required item.',
                },
                onChange: (e) =>
                  setValue('bankAccount', sanitizeInput(e.target.value)),
              })}
              error={errors?.bankAccount}
              margin={[8, 0, 0]}
              disabled={disabled}
            />
            <Typography
              size='12px'
              lineHeight='20px'
              fontWeight='400'
              color='#A7A7A7'
              margin='8px 0 20px 0'
            >
              * Corporate accounts are not accepted.
            </Typography>
            <SwiftCode
              register={register}
              errors={errors}
              disabled={disabled}
            />
            <Input
              {...register('address', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\S[a-zA-Z\s,-]+$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue('address', sanitizeInput(e.target.value)),
              })}
              label='Block/Street Name'
              isRequired
              placeholder='Block/Street Name (English only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.address}
            />
            <Input
              {...register('addressDetail', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('addressDetail', sanitizeInput(e.target.value)),
              })}
              label='Building/House Number'
              isRequired
              placeholder='Building/House Number (English and numbers)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.addressDetail}
            />
            <Input
              {...register('zipPostalCode', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('zipPostalCode', sanitizeInput(e.target.value)),
              })}
              label='Post code'
              isRequired
              placeholder='Post code'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.zipPostalCode}
            />
          </>
        );
      case 'IDN':
        return (
          <>
            <Input
              {...register('bank', {
                required: 'This is a required item.',
                onChange: (e) =>
                  setValue('bank', sanitizeInput(e.target.value)),
              })}
              label='Bank account'
              placeholder='Name of Bank'
              type='text'
              error={errors?.bank}
              disabled={disabled}
              isRequired
            />
            <Input
              {...register('bankAccount', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter only numbers',
                },
                onChange: (e) =>
                  setValue('bankAccount', sanitizeInput(e.target.value)),
              })}
              type='text'
              placeholder='Bank account'
              error={errors?.bankAccount}
              margin={[8, 0, 0]}
              disabled={disabled}
            />
            <Typography
              size='12px'
              lineHeight='20px'
              fontWeight='400'
              color='#A7A7A7'
              margin='8px 0 20px 0'
            >
              * Corporate accounts are not accepted.
            </Typography>
            <SwiftCode
              register={register}
              errors={errors}
              disabled={disabled}
            />
            <Input
              {...register('stateProvinceRegion', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue(
                    'stateProvinceRegion',
                    sanitizeInput(e.target.value),
                  ),
              })}
              label='Province'
              isRequired
              placeholder='Province (English Only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.stateProvinceRegion}
            />
            <Input
              {...register('city', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue('city', sanitizeInput(e.target.value)),
              })}
              label='City or Regency'
              isRequired
              placeholder='City or Regency  (English Only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.city}
            />
            <Input
              {...register('address', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('address', sanitizeInput(e.target.value)),
              })}
              label='Street Name'
              isRequired
              placeholder='Street Name'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.address}
            />
            <Input
              {...register('addressDetail2', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('addressDetail2', sanitizeInput(e.target.value)),
              })}
              label='APT, UNIT or SUITE'
              isRequired
              placeholder='APT, UNIT or SUITE'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.addressDetail2}
            />
            <Input
              {...register('zipPostalCode', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('zipPostalCode', sanitizeInput(e.target.value)),
              })}
              label='Post code'
              isRequired
              placeholder='Post code'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.zipPostalCode}
            />
          </>
        );
      case 'NZL':
        return (
          <>
            <Input
              {...register('bank', {
                required: 'This is a required item.',
                onChange: (e) =>
                  setValue('bank', sanitizeInput(e.target.value)),
              })}
              label='Bank account'
              placeholder='Name of Bank'
              type='text'
              error={errors?.bank}
              disabled={disabled}
              isRequired
            />
            <Input
              {...register('bankAccount', {
                minLength: {
                  value: nationality === 'NZL' ? 15 : 0,
                  message: 'Please enter 15 or 16 digits.',
                },
                required: 'This is a required item.',
                pattern: {
                  value: nationality === 'NZL' ? /^\d+$/ : /^\S/,
                  message: 'Please enter only numbers.',
                },
                onChange: (e) =>
                  setValue('bankAccount', sanitizeInput(e.target.value)),
              })}
              type='text'
              placeholder='Bank account'
              error={errors?.bankAccount}
              margin={[8, 0, 0]}
              disabled={disabled}
              maxLength={nationality === 'NZL' ? 16 : 9999}
            />
            <Typography
              size='12px'
              lineHeight='20px'
              fontWeight='400'
              color='#A7A7A7'
              margin='8px 0 0 0'
            >
              * Corporate accounts are not accepted.
            </Typography>
            {isSwiftCodeRequired && (
              <>
                <Input
                  {...register('city', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z][a-zA-Z\s]*$/,
                      message: 'Please enter only English',
                    },
                  })}
                  label='City or Suburb'
                  isRequired
                  placeholder='City or Suburb (English only)'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.city}
                />
                <Input
                  {...register('address', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                      message: 'Please enter only English and numbers.',
                    },
                    onChange: (e) =>
                      setValue('address', sanitizeInput(e.target.value)),
                  })}
                  label='Street Name & Number'
                  isRequired
                  placeholder='Street Name & Number'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.address}
                />
                <Input
                  {...register('addressDetail', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                      message: 'Please enter only English and numbers.',
                    },
                    onChange: (e) =>
                      setValue('addressDetail', sanitizeInput(e.target.value)),
                  })}
                  label='House No.'
                  isRequired
                  placeholder='House No.'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.addressDetail}
                />
                <Input
                  {...register('zipPostalCode', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                      message: 'Please enter only English and numbers.',
                    },
                    onChange: (e) =>
                      setValue('zipPostalCode', sanitizeInput(e.target.value)),
                  })}
                  label='Post code'
                  isRequired
                  placeholder='Post code'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.zipPostalCode}
                />
              </>
            )}
          </>
        );
      case 'PHL':
        return (
          <>
            <Input
              {...register('bank', {
                required: 'This is a required item.',
                onChange: (e) =>
                  setValue('bank', sanitizeInput(e.target.value)),
              })}
              label='Bank account'
              placeholder='Name of Bank'
              type='text'
              error={errors?.bank}
              disabled={disabled}
              isRequired
            />
            <Input
              {...register('bankAccount', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter only numbers',
                },
                onChange: (e) =>
                  setValue('bankAccount', sanitizeInput(e.target.value)),
              })}
              type='text'
              placeholder='Bank account'
              error={errors?.bankAccount}
              margin={[8, 0, 0]}
              disabled={disabled}
            />
            <SwiftCode
              register={register}
              errors={errors}
              disabled={disabled}
            />
            <Input
              {...register('country', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue('country', sanitizeInput(e.target.value)),
              })}
              label='Country'
              isRequired
              placeholder='Country (English only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.country}
            />
            <Input
              {...register('city', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue('city', sanitizeInput(e.target.value)),
              })}
              label='City'
              isRequired
              placeholder='City (English only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.city}
            />
            <Input
              {...register('address', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('address', sanitizeInput(e.target.value)),
              })}
              label='Street Name'
              isRequired
              placeholder='Street Name'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.address}
            />
            <Input
              {...register('addressDetail2', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('addressDetail2', sanitizeInput(e.target.value)),
              })}
              label='APT, UNIT or SUITE'
              isRequired
              placeholder='APT, UNIT or SUITE'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.addressDetail2}
            />
            <Input
              {...register('zipPostalCode', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('zipPostalCode', sanitizeInput(e.target.value)),
              })}
              label='Post code'
              isRequired
              placeholder='Post code'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.zipPostalCode}
            />
          </>
        );
      case 'RUS':
        return (
          <>
            <Input
              {...register('bank', {
                required: 'This is a required item.',
                onChange: (e) =>
                  setValue('bank', sanitizeInput(e.target.value)),
              })}
              label='банковский счет'
              placeholder='Name of Bank'
              type='text'
              error={errors?.bank}
              disabled={disabled}
              isRequired
            />
            <Input
              {...register('bankAccount', {
                pattern: {
                  value: /\S+/,
                  message: 'This is a required item.',
                },
                onChange: (e) =>
                  setValue('bankAccount', sanitizeInput(e.target.value)),
              })}
              type='text'
              placeholder='Bank account'
              error={errors?.bankAccount}
              margin={[8, 0, 0]}
              disabled={disabled}
            />
            <Input
              {...register('bsbNumber', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('bsbNumber', sanitizeInput(e.target.value)),
              })}
              label='быстрый код'
              isRequired
              placeholder='BIC'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.bsbNumber}
            />
            <Input
              {...register('country', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue('country', sanitizeInput(e.target.value)),
              })}
              label='Страна'
              isRequired
              placeholder='Country (English only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.country}
            />
            <Input
              {...register('city', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue('city', sanitizeInput(e.target.value)),
              })}
              label='Сити'
              isRequired
              placeholder='City (English only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.city}
            />
            <Input
              {...register('address', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('address', sanitizeInput(e.target.value)),
              })}
              label='Название улицы'
              isRequired
              placeholder='Street Name'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.address}
            />
            <Input
              {...register('addressDetail2', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('addressDetail2', sanitizeInput(e.target.value)),
              })}
              label='КВАРТИРА, НОМЕР или ЛЮКС'
              isRequired
              placeholder='APT, UNIT or SUITE'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.addressDetail2}
            />
            <Input
              {...register('zipPostalCode', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('zipPostalCode', sanitizeInput(e.target.value)),
              })}
              label='Post code'
              isRequired
              placeholder='Post code'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.zipPostalCode}
            />
          </>
        );
      case 'ARE':
        return (
          <>
            <Input
              {...register('ibanNumber', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('ibanNumber', sanitizeInput(e.target.value)),
              })}
              label='IBAN Number'
              isRequired
              placeholder='IBAN Number'
              disabled={disabled}
              error={errors?.ibanNumber}
            />
            <Typography
              size='12px'
              lineHeight='20px'
              fontWeight='400'
              color='#A7A7A7'
              margin='8px 0 0 0'
            >
              * IBANs are long account numbers used by banks for cross-border
              transfers. Each country structures this+ number differently, but
              it always starts with a 2 digit country code (e.g. DE for
              Germany).
            </Typography>
            {isSwiftCodeRequired && (
              <>
                <Input
                  {...register('stateProvinceRegion', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z][a-zA-Z\s]*$/,
                      message: 'Please enter only English',
                    },
                  })}
                  label='Region'
                  isRequired
                  placeholder='Region (English only)'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.stateProvinceRegion}
                />
                <Input
                  {...register('city', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z][a-zA-Z\s]*$/,
                      message: 'Please enter only English',
                    },
                  })}
                  label='Town/City'
                  isRequired
                  placeholder='Town/City (English only)'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.city}
                />
                <Input
                  {...register('address', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                      message: 'Please enter only English and numbers.',
                    },
                    onChange: (e) =>
                      setValue('address', sanitizeInput(e.target.value)),
                  })}
                  label='Street Name & Number'
                  isRequired
                  placeholder='Street Name & Number'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.address}
                />
                <Input
                  {...register('addressDetail2', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                      message: 'Please enter only English and numbers.',
                    },
                    onChange: (e) =>
                      setValue('addressDetail2', sanitizeInput(e.target.value)),
                  })}
                  label='APT, UNIT or SUITE'
                  isRequired
                  placeholder='APT, UNIT or SUITE'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.addressDetail2}
                />
                <Input
                  {...register('zipPostalCode', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                      message: 'Please enter only English and numbers.',
                    },
                    onChange: (e) =>
                      setValue('zipPostalCode', sanitizeInput(e.target.value)),
                  })}
                  label='Post code'
                  isRequired
                  placeholder='Post code'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.zipPostalCode}
                />
              </>
            )}
          </>
        );
      case 'CHL':
        return (
          <>
            <Input
              {...register('bank', {
                required: 'This is a required item.',
                onChange: (e) =>
                  setValue('bank', sanitizeInput(e.target.value)),
              })}
              label='Cuenta bancaria'
              placeholder='Name of Bank'
              type='text'
              error={errors?.bank}
              disabled={disabled}
              isRequired
            />
            <Input
              {...register('bankAccount', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\S/,
                  message: 'This is a required item.',
                },
                onChange: (e) =>
                  setValue('bankAccount', sanitizeInput(e.target.value)),
              })}
              type='text'
              placeholder='Bank account'
              error={errors?.bankAccount}
              margin={[8, 0, 0]}
              disabled={disabled}
            />
            <FlexWrap dir='column' margin='20px 0 0 0'>
              <LabelWrapper>
                <Label>Tipo de cuenta</Label>
                <RequiredDot>*</RequiredDot>
              </LabelWrapper>
              <FlexWrap dir='column' justifyContent='center' gap='16px'>
                <RadioLabel>
                  <input
                    {...register('accountInfo', {
                      required: 'This is a required item.',
                    })}
                    type='radio'
                    value='CHECKING'
                    onClick={() => setSelectedAccountInfo('CHECKING')}
                    checked={selectedAccountInfo === 'CHECKING'}
                    disabled={disabled}
                  />
                  Checking [cuenta corriente]
                </RadioLabel>
                <RadioLabel>
                  <input
                    {...register('accountInfo', {
                      required: 'This is a required item.',
                    })}
                    type='radio'
                    value='SAVING'
                    onClick={() => setSelectedAccountInfo('SAVING')}
                    checked={selectedAccountInfo === 'SAVING'}
                    disabled={disabled}
                  />
                  Savings [cuenta de ahorro]
                </RadioLabel>
                <RadioLabel>
                  <input
                    {...register('accountInfo', {
                      required: 'This is a required item.',
                    })}
                    type='radio'
                    value='DEMAND'
                    onClick={() => setSelectedAccountInfo('DEMAND')}
                    checked={selectedAccountInfo === 'DEMAND'}
                    disabled={disabled}
                  />
                  Demand [cuenta vista]
                </RadioLabel>
                {errors?.accountInfo && (
                  <FlexWrap gap='8px'>
                    <Icon imgUrl={ICON_WARNING} width='18px' height='18px' />
                    <Typography color='#f13e4b' size='13px' fontWeight='400'>
                      This is a required item.
                    </Typography>
                  </FlexWrap>
                )}
              </FlexWrap>
            </FlexWrap>
            <Input
              {...register('institutionNumber', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('institutionNumber', sanitizeInput(e.target.value)),
              })}
              type='text'
              isRequired
              label='Rol Único Tributario'
              placeholder="Recipient's RUT number"
              error={errors?.institutionNumber}
              margin={[20, 0, 0]}
              disabled={disabled}
            />
            {isSwiftCodeRequired && (
              <>
                <Input
                  {...register('stateProvinceRegion', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z][a-zA-Z\s]*$/,
                      message: 'Please enter only English',
                    },
                  })}
                  label='Region'
                  isRequired
                  placeholder='Region (English only)'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.stateProvinceRegion}
                />
                <Input
                  {...register('city', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z][a-zA-Z\s]*$/,
                      message: 'Please enter only English',
                    },
                  })}
                  label='Town/City'
                  isRequired
                  placeholder='Town/City (English only)'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.city}
                />
                <Input
                  {...register('address', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                      message: 'Please enter only English and numbers.',
                    },
                    onChange: (e) =>
                      setValue('address', sanitizeInput(e.target.value)),
                  })}
                  label='Street Name & Number'
                  isRequired
                  placeholder='Street Name & Number'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.address}
                />
                <Input
                  {...register('addressDetail2', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                      message: 'Please enter only English and numbers.',
                    },
                    onChange: (e) =>
                      setValue('addressDetail2', sanitizeInput(e.target.value)),
                  })}
                  label='APT, UNIT or SUITE'
                  isRequired
                  placeholder='APT, UNIT or SUITE'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.addressDetail2}
                />
                <Input
                  {...register('zipPostalCode', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                      message: 'Please enter only English and numbers.',
                    },
                    onChange: (e) =>
                      setValue('zipPostalCode', sanitizeInput(e.target.value)),
                  })}
                  label='Post code'
                  isRequired
                  placeholder='Post code'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.zipPostalCode}
                />
              </>
            )}
          </>
        );
      case 'MYS':
        return (
          <>
            <Input
              {...register('bank', {
                required: 'This is a required item.',
                onChange: (e) =>
                  setValue('bank', sanitizeInput(e.target.value)),
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
              })}
              label='Bank account'
              placeholder='Name of Bank'
              type='text'
              error={errors?.bank}
              disabled={disabled}
              isRequired
            />
            <Input
              {...register('bankAccount', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter only numbers',
                },
                onChange: (e) =>
                  setValue('bankAccount', sanitizeInput(e.target.value)),
              })}
              type='text'
              placeholder='Bank account'
              error={errors?.bankAccount}
              margin={[8, 0, 0]}
              disabled={disabled}
            />
            <Typography
              size='12px'
              lineHeight='20px'
              fontWeight='400'
              color='#A7A7A7'
              margin='8px 0 0 0'
            >
              * Corporate accounts are not accepted.
            </Typography>
            <SwiftCode
              register={register}
              errors={errors}
              disabled={disabled}
            />
            <Input
              {...register('stateProvinceRegion', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue(
                    'stateProvinceRegion',
                    sanitizeInput(e.target.value),
                  ),
              })}
              label='Province'
              isRequired
              placeholder='Province (English Only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.stateProvinceRegion}
            />
            <Input
              {...register('city', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue('city', sanitizeInput(e.target.value)),
              })}
              label='City or Suburb'
              isRequired
              placeholder='City or Suburb (English only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.city}
            />
            <Input
              {...register('address', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('address', sanitizeInput(e.target.value)),
              })}
              label='Street Name'
              isRequired
              placeholder='Street Name'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.address}
            />
            <Input
              {...register('addressDetail2', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('addressDetail2', sanitizeInput(e.target.value)),
              })}
              label='APT, UNIT or SUITE'
              isRequired
              placeholder='APT, UNIT or SUITE'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.addressDetail2}
            />
            <Input
              {...register('zipPostalCode', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter only numbers',
                },
                onChange: (e) =>
                  setValue('zipPostalCode', sanitizeInput(e.target.value)),
              })}
              label='Post code'
              isRequired
              placeholder='Post code'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.zipPostalCode}
            />
          </>
        );
      case 'VNM':
        return (
          <>
            <Input
              {...register('bank', {
                required: 'This is a required item.',
                onChange: (e) =>
                  setValue('bank', sanitizeInput(e.target.value)),
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
              })}
              label='Bank account'
              placeholder='Name of Bank'
              type='text'
              error={errors?.bank}
              disabled={disabled}
              isRequired
            />
            <Input
              {...register('bankAccount', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter only numbers',
                },
                onChange: (e) =>
                  setValue('bankAccount', sanitizeInput(e.target.value)),
              })}
              type='text'
              placeholder='Bank account'
              error={errors?.bankAccount}
              margin={[8, 0, 0]}
              disabled={disabled}
            />
            <Typography
              size='12px'
              lineHeight='20px'
              fontWeight='400'
              color='#A7A7A7'
              margin='8px 0 0 0'
            >
              * Corporate accounts are not accepted.
            </Typography>
            <SwiftCode
              register={register}
              errors={errors}
              disabled={disabled}
            />
            <Input
              {...register('stateProvinceRegion', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue(
                    'stateProvinceRegion',
                    sanitizeInput(e.target.value),
                  ),
              })}
              label='Region'
              isRequired
              placeholder='Region (English only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.stateProvinceRegion}
            />
            <Input
              {...register('city', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
              })}
              label='District'
              isRequired
              placeholder='District (English only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.city}
            />
            <Input
              {...register('address', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
              })}
              label='Sub-district / Street Name'
              isRequired
              placeholder='Sub-district / Street Name (English only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.address}
            />
            <Input
              {...register('addressDetail', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('addressDetail', sanitizeInput(e.target.value)),
              })}
              label='House No.'
              isRequired
              placeholder='House No.'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.addressDetail}
            />
            <Input
              {...register('zipPostalCode', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter only numbers',
                },
                onChange: (e) =>
                  setValue('zipPostalCode', sanitizeInput(e.target.value)),
              })}
              label='Post code'
              isRequired
              placeholder='Post code'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.zipPostalCode}
            />
          </>
        );
      case 'IND':
        return (
          <>
            <Input
              {...register('swiftCode', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                minLength: {
                  value: 11,
                  message: 'Please enter 11 digits.',
                },
                onChange: (e) =>
                  setValue('swiftCode', sanitizeInput(e.target.value)),
              })}
              label='IFSC Code'
              isRequired
              maxLength={11}
              placeholder='IFSC Code'
              disabled={disabled}
              error={errors?.swiftCode}
            />
            <Typography
              size='12px'
              lineHeight='20px'
              fontWeight='400'
              color='#A7A7A7'
              margin='8px 0 0 0'
            >
              * The Indian Financial System Code (IFSC) identifies the Indian
              Bank branch. It’s 11 characters long, and used for RTGS and NEFT
              transfers.
            </Typography>
            <Input
              {...register('bankAccount', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('bankAccount', sanitizeInput(e.target.value)),
              })}
              type='text'
              isRequired
              label='Account Number'
              placeholder='Account Number'
              error={errors?.bankAccount}
              margin={[20, 0, 0]}
              disabled={disabled}
            />
            {isSwiftCodeRequired && (
              <>
                <Input
                  {...register('stateProvinceRegion', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z][a-zA-Z\s]*$/,
                      message: 'Please enter only English',
                    },
                  })}
                  label='State '
                  isRequired
                  placeholder='State  (English only)'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.stateProvinceRegion}
                />
                <Input
                  {...register('city', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z][a-zA-Z\s]*$/,
                      message: 'Please enter only English',
                    },
                  })}
                  label='City or Suburb'
                  isRequired
                  placeholder='City or Suburb (English only)'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.city}
                />
                <Input
                  {...register('address', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                      message: 'Please enter only English and numbers.',
                    },
                    onChange: (e) =>
                      setValue('address', sanitizeInput(e.target.value)),
                  })}
                  label='Street Name & Number'
                  isRequired
                  placeholder='Street Name & Number'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.address}
                />
                <Input
                  {...register('addressDetail', {
                    pattern: {
                      value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                      message: 'Please enter only English and numbers.',
                    },
                  })}
                  label='Recipient Address'
                  placeholder='Recipient Address'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.addressDetail}
                />
                <Input
                  {...register('zipPostalCode', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                      message: 'Please enter only English and numbers.',
                    },
                    onChange: (e) =>
                      setValue('zipPostalCode', sanitizeInput(e.target.value)),
                  })}
                  label='Post code'
                  isRequired
                  placeholder='Post code'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.zipPostalCode}
                />
              </>
            )}
          </>
        );
      case 'MEX':
        return (
          <>
            <Input
              {...register('bankAccount', {
                required: 'This is a required item.',
                pattern: {
                  value: /^\d+$/,
                  message: 'Please enter only numbers',
                },
                minLength: {
                  value: 18,
                  message: 'Please enter 18 digits.',
                },
                onChange: (e) =>
                  setValue('bankAccount', sanitizeInput(e.target.value)),
              })}
              label='CLABE'
              isRequired
              maxLength={18}
              placeholder='CLABE'
              disabled={disabled}
              error={errors?.bankAccount}
            />
            <Typography
              size='12px'
              lineHeight='20px'
              fontWeight='400'
              color='#A7A7A7'
              margin='8px 0 0 0'
            >
              * CLABE (Clave Bancaria Estandarizada) is the standard format for
              bank account numbers in Mexico. A bank account's CLABE code is 18
              digits long.
            </Typography>
            <Input
              {...register('citizenId', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                minLength: {
                  value: 18,
                  message: 'Please enter 18 digits.',
                },
                onChange: (e) =>
                  setValue('citizenId', sanitizeInput(e.target.value)),
              })}
              label='CURP'
              isRequired
              maxLength={18}
              placeholder='CURP'
              disabled={disabled}
              margin={[20, 0, 0]}
              error={errors?.citizenId}
            />
            <Typography
              size='12px'
              lineHeight='20px'
              fontWeight='400'
              color='#A7A7A7'
              margin='8px 0 0 0'
            >
              * In order to speed up your transfer we recommend you to fill this
              field. The CURP (Clave Única de Registro de Población) is a unique
              registration number for private persons in Mexico.
            </Typography>
            {isSwiftCodeRequired && (
              <>
                <Input
                  {...register('stateProvinceRegion', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z][a-zA-Z\s]*$/,
                      message: 'Please enter only English',
                    },
                  })}
                  label='Province'
                  isRequired
                  placeholder='Province (English only)'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.stateProvinceRegion}
                />
                <Input
                  {...register('city', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z][a-zA-Z\s]*$/,
                      message: 'Please enter only English',
                    },
                  })}
                  label='City or Suburb'
                  isRequired
                  placeholder='City or Suburb (English only)'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.city}
                />
                <Input
                  {...register('address', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                      message: 'Please enter only English and numbers.',
                    },
                    onChange: (e) =>
                      setValue('address', sanitizeInput(e.target.value)),
                  })}
                  label='Street Name & Number'
                  isRequired
                  placeholder='Street Name & Number'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.address}
                />
                <Input
                  {...register('addressDetail2', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                      message: 'Please enter only English and numbers.',
                    },
                    onChange: (e) =>
                      setValue('addressDetail2', sanitizeInput(e.target.value)),
                  })}
                  label='APT, UNIT or SUITE'
                  isRequired
                  placeholder='APT, UNIT or SUITE'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.addressDetail2}
                />
                <Input
                  {...register('zipPostalCode', {
                    required: 'This is a required item.',
                    pattern: {
                      value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                      message: 'Please enter only English and numbers.',
                    },
                    onChange: (e) =>
                      setValue('zipPostalCode', sanitizeInput(e.target.value)),
                  })}
                  label='Post code'
                  isRequired
                  placeholder='Post code'
                  margin={[20, 0, 0]}
                  disabled={disabled}
                  error={errors?.zipPostalCode}
                />
              </>
            )}
          </>
        );
      // 공통 입력폼
      case 'TUR':
      case 'AUT':
      case 'SWE':
      case 'POL':
      case 'PRT':
      case 'ISL':
      case 'FIN':
      case 'ROU':
      case 'GRC':
      case 'CHE':
      case 'DEU':
      case 'NLD':
      case 'ITA':
      case 'GBR':
      case 'FRA':
        return (
          <>
            <Input
              {...register('ibanNumber', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('ibanNumber', sanitizeInput(e.target.value)),
              })}
              label='IBAN Number'
              isRequired
              placeholder='IBAN Number'
              disabled={disabled}
              error={errors?.ibanNumber}
            />
            <Typography
              size='12px'
              lineHeight='20px'
              fontWeight='400'
              color='#A7A7A7'
              margin='8px 0 0 0'
            >
              * IBANs are long account numbers used by banks for cross-border
              transfers. Each country structures this number differently, but it
              always starts with a 2 digit country code (e.g. DE for Germany).
            </Typography>
            <SwiftCode
              register={register}
              errors={errors}
              disabled={disabled}
            />
            <Input
              {...register('stateProvinceRegion', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English',
                },
                onChange: (e) =>
                  setValue(
                    'stateProvinceRegion',
                    sanitizeInput(e.target.value),
                  ),
              })}
              label='Region'
              isRequired
              placeholder='Region (English only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.stateProvinceRegion}
            />
            <Input
              {...register('city', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z][a-zA-Z\s]*$/,
                  message: 'Please enter only English.',
                },
                onChange: (e) =>
                  setValue('city', sanitizeInput(e.target.value)),
              })}
              label='Town/City'
              isRequired
              placeholder='Town/City (English only)'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.city}
            />
            <Input
              {...register('address', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('address', sanitizeInput(e.target.value)),
              })}
              label='Street Name'
              isRequired
              placeholder='Street Name'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.address}
            />
            <Input
              {...register('addressDetail', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('addressDetail', sanitizeInput(e.target.value)),
              })}
              label='House No.'
              isRequired
              placeholder='House No.'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.addressDetail}
            />
            <Input
              {...register('zipPostalCode', {
                required: 'This is a required item.',
                pattern: {
                  value: /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/,
                  message: 'Please enter only English and numbers.',
                },
                onChange: (e) =>
                  setValue('zipPostalCode', sanitizeInput(e.target.value)),
              })}
              label='Post code'
              isRequired
              placeholder='Post code'
              margin={[20, 0, 0]}
              disabled={disabled}
              error={errors?.zipPostalCode}
            />
          </>
        );
      default:
        // 캐시 픽업 국가들은 2번째 필드에서 정보 받음
        if (CASH_PICKUP_NATIONS.includes(nationality) && !isRefundInfoPage) {
          return <CashPickupGuideContent textAlign='left' />;
        }
        return <></>;
    }
  };

  return (
    <>
      <ContactWrapper>{remittanceMethodInfo()}</ContactWrapper>
    </>
  );
};

const ContactWrapper = styled.div``;
const LabelWrapper = styled.div<{ marginTop?: string }>`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  margin-top: ${(props) => props.marginTop && props.marginTop};
`;

const Label = styled.label`
  font-size: 16px;
  line-height: 26px;
  font-weight: 400;
  @media ${(props) => props.theme.mobile} {
    font-size: 16px;
    line-height: 24px;
  }
`;

const RequiredDot = styled.div`
  color: #f13e4b;
  font-weight: 400;
  font-size: 20px;
  line-height: 28px;
  margin-left: 4px;
  padding-bottom: 6px;
`;

const BankWrap = styled(FlexWrap)<{ isChnBank?: boolean }>`
  position: relative;
  align-items: center;
  margin-bottom: 8px;
  ${(props) => props.isChnBank && css``}
`;

const ArrowIcon = styled.img<{ isChnBank?: boolean }>`
  width: 24px;
  height: 24px;
  position: absolute;
  right: 13px;
  ${(props) => props.isChnBank && css``}
`;

const Option = styled.option``;
const RadioLabel = styled.label`
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #212121;
`;

const Select = styled.select<{
  isChnBank?: boolean;
  isSelected?: boolean;
  isEdit?: boolean;
  isBankCardInquiry?: boolean;
}>`
  width: 100%;
  border: 1px solid #cbccce;
  cursor: pointer;
  padding: 14px 13px;
  border-radius: 4px;
  appearance: none;
  font-weight: 400;
  line-height: 24px;
  font-size: 14px;
  color: ${(props) =>
    props.isEdit ? '#3A3B3E' : props.isSelected ? '#3A3B3E' : '#CBCCCE'};
  ${(props) =>
    props.isChnBank &&
    css`
      background-color: #f5f6f7;
      font-weight: 500;
      color: #3a3b3e;
      cursor: default;
    `}
  :disabled {
    background-color: #f5f6f7;
  }
`;
